import React, { useEffect, useRef, useState } from 'react'
import './FAQAccordionItem.scss'

interface FAQAccordionItemProps {
  id: string
  activeAccordionId?: string
  isOpen?: boolean
  headingTag?: keyof JSX.IntrinsicElements
  children?: React.ReactNode[]
  setActiveAccordionId: (id: string) => void
}

export const FAQAccordionItem: React.FC<FAQAccordionItemProps> = ({
  id,
  activeAccordionId,
  isOpen,
  headingTag: HeadingTag = 'h2',
  children,
  setActiveAccordionId,
}) => {
  const [expanded, setExpanded] = useState(isOpen)
  const [bodyMaxHeight, setBodyMaxHeight] = useState('0px')
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (expanded && id === activeAccordionId) {
      setBodyMaxHeight(`${bodyRef.current?.scrollHeight}px`)
    } else {
      setBodyMaxHeight('0px')
    }
  }, [expanded, id, activeAccordionId])

  const headerId = `faq-accordion-item-header-${id}`
  const bodyId = `faq-accordion-item-body-${id}`

  const onClick = () => {
    setActiveAccordionId(id)
    setExpanded(!expanded)
  }

  useEffect(() => {
    if (id !== activeAccordionId) {
      setExpanded(false)
    }
  }, [id, activeAccordionId])

  return (
    <section
      className={`faq-accordion-item ${
        id === activeAccordionId && expanded ? 'faq-accordion-item--open' : ''
      }`}
    >
      <HeadingTag className="faq-accordion-item__heading">
        <button
          id={headerId}
          className="faq-accordion-item__button"
          aria-label={
            id === activeAccordionId && expanded
              ? 'close accordion'
              : 'open accordion'
          }
          aria-expanded={
            id === activeAccordionId && expanded ? 'true' : 'false'
          }
          aria-controls={bodyId}
          onClick={onClick}
        >
          {children && children[0]}
          <span className="faq-accordion-item__icon"></span>
        </button>
      </HeadingTag>

      <article
        id={bodyId}
        className="faq-accordion-item__body"
        style={{ maxHeight: bodyMaxHeight }}
        role="region"
        aria-labelledby={headerId}
        ref={bodyRef}
      >
        {children && children[1]}
      </article>
    </section>
  )
}
